import { useState, Fragment, useEffect } from "react";
import Popup from "../../../hoc/Popup/Popup";
import {
  IRehabBooking,
  IRehabSummary,
  IRehabSummaryItem,
  IUser,
} from "../../../types/api";
import "./HandleRehabTimesPopup.scss";
import { capitalizeWords, formatDateToSwedish } from "../../../shared/utility";
import greenCheck from "../../../assets/images/green-check.svg";
import { CognitoUser } from "../../../types/cognito";
import { toast } from "react-toastify";
import { formatDate } from "../../../shared/dateUtils";
import { getUserByMemberId } from "../../../api/users/users";
import {
  cancelRehabBooking,
  createRehabBooking,
  getActiveRehabBookingsByMember,
} from "../../../api/rehab/rehab";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";

interface IHandleRehabTimesPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: () => void;
  rehabSummary?: IRehabSummary;
  date: Date;
  user: CognitoUser | null;
  selectedUser?: IUser | null;
}

function HandleRehabTimesPopup(props: IHandleRehabTimesPopupProps) {
  const {
    rehabSummary,
    date,
    showPopup,
    onClose,
    user,
    selectedUser,
    onConfirm,
  } = props;

  const [showConfirmBookRehabPopup, setShowConfirmBookRehabPopup] =
    useState(false);
  const [showCancelRehabPopup, setShowCancelRehabPopup] = useState(false);
  const [selectedSlotToCancel, setSelectedSlotToCancel] =
    useState<IRehabSummaryItem | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { weekday, dateAndMonth } = formatDateToSwedish(date);
  const [selectedSlot, setSelectedSlot] = useState<IRehabSummaryItem | null>(
    null
  );

  const [activeRehabBookingsForMember, setActiveRehabBookingsForMember] =
    useState<IRehabBooking[]>([]);
  const [usersData, setUsersData] = useState<IUser[]>([]);

  const fetchActiveRehabBookingsForMember = async () => {
    if (selectedUser === null || selectedUser === undefined) {
      return;
    }
    try {
      const response = await getActiveRehabBookingsByMember(
        selectedUser.member_id,
        true
      );
      setActiveRehabBookingsForMember(response);
    } catch (error: any) {
      toast.error(error.errors[0].message);
    }
  };

  useEffect(() => {
    if (selectedUser !== null && showPopup) {
      fetchActiveRehabBookingsForMember();
    }
  }, [selectedUser, showPopup]);
  const handleClose = () => {
    setSelectedSlot(null);
    setIsReady(false);
    onClose();
  };

  const getEndTime = (startTime: string) => {
    const hours = parseInt(startTime.slice(0, 2));
    const minutes = parseInt(startTime.slice(3, 5));

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    date.setMinutes(date.getMinutes() + 15);

    const newHours = date.getHours().toString().padStart(2, "0");
    const newMinutes = date.getMinutes().toString().padStart(2, "0");

    return `${newHours}:${newMinutes}`;
  };

  const handleFetchBookerData = async (memberIds: string[]) => {
    const promises = memberIds.map((memberId) => getUserByMemberId(memberId));
    const response = await Promise.all(promises);
    setUsersData(response);
  };
  useEffect(() => {
    if (props.showPopup === true) {
      let memberIds: string[] = [];
      rehabSummary?.slots.forEach((slot) => {
        if (slot.booking !== null) {
          memberIds.push(slot.booking.member_id);
        }
      });
      handleFetchBookerData(memberIds);
    }
  }, [props.showPopup]);

  const handleConfirmBooking = async () => {
    if (selectedSlot?.start_time === undefined) {
      return;
    }
    if (activeRehabBookingsForMember.length > 0) {
      toast.error("Du kan max ha en aktiv bokning i taget");
      return;
    }
    setIsLoading(true);
    const loadToast = toast.loading("Bokar tid...");
    const bookingToCreate = {
      datetime_start: `${formatDate(date, "yyyy-MM-dd")}T${
        selectedSlot?.start_time
      }`,
      datetime_end: `${formatDate(date, "yyyy-MM-dd")}T${
        getEndTime(selectedSlot?.start_time) + ":00"
      }`,
      flag: 1,
      member_id: selectedUser?.member_id || "",
    };

    if (bookingToCreate.member_id === "") {
      return;
    }

    let sendEmail = false;
    try {
      const response = await createRehabBooking(
        bookingToCreate,
        sendEmail,
        true
      );
      if (response && !("message" in response)) {
        setIsLoading(false);
        toast.dismiss(loadToast);
        toast.success("Rehab tid bokad");
        onConfirm();
        fetchActiveRehabBookingsForMember();
      }
    } catch (error: any) {
      setIsLoading(false);
      toast.dismiss(loadToast);
      toast.error(error);
    }
  };

  const handleCancelRehabBooking = async (rehabBooking: IRehabBooking) => {
    let sendEmail = false;
    try {
      const response = await cancelRehabBooking(rehabBooking, sendEmail, true);
      if (response && !("messsage" in response)) {
        toast.success("Rehab avbokad");
        onConfirm();
        fetchActiveRehabBookingsForMember();
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  return (
    <Popup
      showPopup={showPopup}
      onClose={handleClose}
      leftTopElement={<span className="text-m">Bokade tider</span>}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {selectedUser && (
            <>
              <div className="general-popup-form-top">
                {isReady && (
                  <button
                    className="text-m"
                    onClick={() => {
                      if (activeRehabBookingsForMember.length > 0) {
                        toast.error("Bokare har redan en aktiv rehabbokning");
                        return;
                      }
                      !isLoading && setShowConfirmBookRehabPopup(true);
                    }}
                  >
                    Boka tid
                  </button>
                )}
              </div>
              <div className="general-popup-form-group mb-20">
                <div className="general-popup-form-field">
                  <div className="general-popup-flex-between">
                    <span className="text-m primary-text">Inne som</span>
                    <div className="text-m primary-text text-svg-wrapper">
                      <span>
                        {capitalizeWords(selectedUser.first_name) +
                          " " +
                          capitalizeWords(selectedUser.last_name) +
                          " (" +
                          selectedUser.member_id +
                          ")"}
                      </span>
                      <img src={greenCheck} alt="green check"></img>
                    </div>
                  </div>
                  <div className="general-popup-flex-between">
                    <span className="text-m primary-text">
                      {activeRehabBookingsForMember.length}{" "}
                      {activeRehabBookingsForMember.length !== 1 ? (
                        <> aktiva rehabbokningar</>
                      ) : (
                        <> aktiv rehabbokning</>
                      )}
                    </span>
                    <div className="text-m">
                      den{" "}
                      {activeRehabBookingsForMember.length &&
                        activeRehabBookingsForMember[0].datetime_start.slice(
                          0,
                          10
                        )}{" "}
                      kl.{" "}
                      {activeRehabBookingsForMember.length &&
                        activeRehabBookingsForMember[0].datetime_start.slice(
                          11,
                          16
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="general-popup-form-top"></div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {weekday}, {dateAndMonth}
                  </span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Vald tid</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {selectedSlot
                      ? selectedSlot.start_time.slice(0, 5) +
                        " - " +
                        getEndTime(selectedSlot.start_time).slice(0, 5)
                      : ""}
                  </span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
            </div>
          </div>
          <div className="rehab-slots-wrapper">
            <span className="text-m">Tider</span>
            {rehabSummary &&
              rehabSummary.slots.map((slot, index) => {
                const selectedClass =
                  selectedSlot?.start_time === slot.start_time
                    ? "selected"
                    : "";
                const isInPast =
                  formatDate(props.date, "yyyy-MM-dd") <
                    formatDate(new Date(), "yyyy-MM-dd") ||
                  (slot.start_time < formatDate(new Date(), "HH:mm:ss") &&
                    formatDate(props.date, "yyyy-MM-dd") ===
                      formatDate(new Date(), "yyyy-MM-dd"));
                if (slot.booking !== null) {
                  let user = usersData.find(
                    (user) => user.member_id === slot.booking.member_id
                  );
                  if (slot.booking.member_id === selectedUser?.member_id) {
                    user = selectedUser;
                  }
                  return (
                    <div
                      key={slot.start_time}
                      className={`rehab-slot-wrapper text-m my-booking`}
                    >
                      <div className="rehab-slot-time">
                        <span>
                          {slot.start_time.slice(0, 5)}
                          {" - " + getEndTime(slot.start_time).slice(0, 5)}
                        </span>
                        <div className="rehab-slot-user-action">
                          <span>
                            {capitalizeWords(user?.first_name || "") +
                              " " +
                              capitalizeWords(user?.last_name || "") +
                              " (" +
                              slot.booking.member_id +
                              ")"}
                          </span>
                          <span
                            className="pointer text-danger"
                            onClick={() => {
                              setShowCancelRehabPopup(true);
                              setSelectedSlotToCancel(slot);
                            }}
                          >
                            Avboka
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                } else if (isInPast) {
                } else {
                  return (
                    <div
                      key={slot.start_time}
                      className={`rehab-slot-wrapper pointer text-m ${selectedClass}`}
                      onClick={() => {
                        toast.success(`${slot.start_time.slice(0, 5)} vald`);
                        setSelectedSlot(slot);
                        setIsReady(true);
                      }}
                    >
                      <div className="rehab-slot-time">
                        <span>
                          {slot.start_time.slice(0, 5)}
                          {" - " + getEndTime(slot.start_time).slice(0, 5)}
                        </span>
                      </div>
                    </div>
                  );
                }
              })}
          </div>
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showConfirmBookRehabPopup}
        onClose={() => setShowConfirmBookRehabPopup(false)}
        onConfirm={() => {
          setShowConfirmBookRehabPopup(false);
          handleConfirmBooking();
        }}
        confirmText="Boka tid"
        denyText="Avbryt"
        questionText="Är du säker på att du vill boka denna tid?"
      />

      <HandleConfirmationPopup
        showPopup={showCancelRehabPopup}
        onClose={() => setShowCancelRehabPopup(false)}
        onConfirm={() => {
          setShowCancelRehabPopup(false);
          if (selectedSlotToCancel) {
            handleCancelRehabBooking(selectedSlotToCancel.booking);
          }
        }}
        confirmText="Avboka tid"
        denyText="Avbryt"
        questionText="Är du säker på att du vill avboka denna tid?"
      />
    </Popup>
  );
}

export default HandleRehabTimesPopup;
